import React, { useState, useEffect } from 'react';
import './Pages.css';

const PAYMENT_METHODS = [
  { value: 'transaction', icon: '🧾', label: 'Already Paid', description: 'Validate an existing M-Pesa transaction code', definiteOnly: false },
  { value: 'mpesa', icon: '📱', label: 'Mpesa Prompt', description: 'Send payment request to customer', definiteOnly: false },
  { value: 'wallet', icon: '💼', label: 'From Wallet', description: 'Pay from the agency wallet/float balance', definiteOnly: true, balanceField: 'walletBalance' },
  { value: 'credit', icon: '🏦', label: 'Credit Facility', description: 'Book against the underwriter credit facility', definiteOnly: true, balanceField: 'creditLimit' },
  { value: 'remit_later', icon: '🕒', label: 'Submit Premiums Later', description: 'Approve now, premium stays outstanding until submitted', definiteOnly: false }
];

const PendingExtensions = ({ userRole }) => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionCode, setTransactionCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [definiteBalances, setDefiniteBalances] = useState(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);

  useEffect(() => {
    fetchPendingExtensions();
  }, []);

  const isDefiniteUnderwriter = /definite/i.test(selectedExtension?.UnderwriterName || '');

  useEffect(() => {
    if (!showPaymentModal || !isDefiniteUnderwriter) {
      setDefiniteBalances(null);
      return;
    }

    const fetchDefiniteBalances = async () => {
      setLoadingBalances(true);
      try {
        const response = await fetch('https://main.victriedemos.com:3017/api/definite/balances');
        const data = await response.json();
        if (data.success) {
          setDefiniteBalances(data);
        }
      } catch (err) {
        console.error('Error fetching Definite Assurance balances:', err);
      } finally {
        setLoadingBalances(false);
      }
    };

    fetchDefiniteBalances();
  }, [showPaymentModal, isDefiniteUnderwriter]);

  const fetchPendingExtensions = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/extensions/status/3');
      const data = await response.json();

      if (data.success) {
        setExtensions(data.data);
      } else {
        setError('Failed to load pending extensions');
      }
    } catch (err) {
      console.error('Error fetching pending extensions:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const handleApproveExtension = async (extension) => {
    setSelectedExtension(extension);
    console.log('Selected Extension:', extension);
    setShowPaymentModal(true);
    setPaymentMethod('');
    setTransactionCode('');
    setPhoneNumber('');
    setApprovalResult(null);
    setSubmissionError(null);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedExtension(null);
    setPaymentMethod('');
    setTransactionCode('');
    setPhoneNumber('');
    setApprovalResult(null);
    setSubmissionError(null);
  };

  const handleSubmitPayment = async () => {
    if (!paymentMethod) {
      alert('Please select a payment method');
      return;
    }

    if (paymentMethod === 'transaction' && !transactionCode) {
      alert('Please enter transaction code');
      return;
    }

    if (paymentMethod === 'mpesa' && !phoneNumber) {
      alert('Please enter phone number');
      return;
    }

    setSubmitting(true);
    setSubmissionError(null);

    try {
      const userEmail = localStorage.getItem('userEmail');

      const response = await fetch('https://main.victriedemos.com:3017/api/extensions/approve-with-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extensionId: selectedExtension.Id,
          grossPremium: selectedExtension.GrossPremium,
          commission: selectedExtension.Commission,
          paymentMethod: paymentMethod,
          transactionCode: transactionCode,
          phoneNumber: phoneNumber,
          approvedBy: userEmail
        })
      });

      const data = await response.json();

      if (data.success) {
        let warning = null;
        if (data.definitePayment && data.definitePayment.success === false) {
          warning = 'Definite Assurance payment was not recorded: ' + data.definitePayment.message;
        } else if (data.definitePayment && data.definitePayment.certificateError) {
          warning = 'Certificate could not be generated: ' + data.definitePayment.certificateError;
        }

        setApprovalResult({
          message: data.message,
          warning,
          certNo: data.definitePayment?.certUrl ? data.definitePayment.certNo : null,
          certUrl: data.definitePayment?.certUrl || null
        });
        fetchPendingExtensions();
      } else {
        setSubmissionError(data.message);
      }
    } catch (err) {
      console.error('Error approving extension:', err);
      setSubmissionError('An error occurred while processing payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectExtension = async (extension) => {
    const reason = prompt(`Reject extension for policy ${extension.PolicyNumber}?\n\nPlease provide a reason for rejection:`);
    
    if (!reason || reason.trim() === '') {
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail');
      
      const response = await fetch('https://main.victriedemos.com:3017/api/extensions/reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extensionId: extension.Id,
          rejectedBy: userEmail,
          reason: reason
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Extension rejected and returned to new extensions!');
        fetchPendingExtensions();
      } else {
        alert('❌ Failed to reject extension: ' + data.message);
      }
    } catch (err) {
      console.error('Error rejecting extension:', err);
      alert('❌ An error occurred while rejecting extension');
    }
  };

  const filteredExtensions = extensions.filter(ext => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const policyNumber = (ext.PolicyNumber || '').toLowerCase();
    const registration = (ext.Registration || '').toLowerCase();
    
    return policyNumber.includes(search) || registration.includes(search);
  });

  const isAdmin = userRole === '1' || userRole === 1;

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading pending extensions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>⏳ Extensions Awaiting Approval</h1>
          <p className="page-subtitle">Policy extensions submitted for management approval</p>
        </div>
        <div className="badge-count">{filteredExtensions.length} Pending</div>
      </div>

      {!isAdmin && (
        <div className="info-banner">
          ℹ️ Only administrators can approve or reject extensions
        </div>
      )}

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Search by Policy Number or Registration..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="clear-search-btn"
            onClick={() => setSearchTerm('')}
          >
            ✕ Clear
          </button>
        )}
      </div>

      <div className="table-container">
        {filteredExtensions.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? `No extensions found matching "${searchTerm}"` : 'No extensions awaiting approval'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Policy Number</th>
                <th>Registration</th>
                <th>Commencement Date</th>
                <th>Expiry Date</th>
                <th>Gross Premium</th>
                <th>Date Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExtensions.map((ext, index) => (
                <tr key={ext.Id}>
                  <td>{index + 1}</td>
                  <td className="policy-number">{ext.PolicyNumber || 'N/A'}</td>
                  <td>{ext.Registration || 'N/A'}</td>
                  <td>{formatDate(ext.CommencementDate)}</td>
                  <td>{formatDate(ext.ExpiryDate)}</td>
                  <td className="amount">{formatCurrency(ext.GrossPremium)}</td>
                  <td>{formatDate(ext.DateDone)}</td>
                  <td>
                    {isAdmin ? (
                      <>
                        <button 
                          className="action-icon-btn approve-btn" 
                          title="Approve Extension"
                          onClick={() => handleApproveExtension(ext)}
                        >
                          ✅
                        </button>
                        <button 
                          className="action-icon-btn reject-btn" 
                          title="Reject Extension"
                          onClick={() => handleRejectExtension(ext)}
                        >
                          ❌
                        </button>
                      </>
                    ) : (
                      <span className="text-muted">No actions</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedExtension && (
        <div className="modal-overlay" onClick={handleClosePaymentModal}>
          <div className="modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💳 Payment Confirmation</h2>
              <button className="modal-close-btn" onClick={handleClosePaymentModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="payment-info">
                <p>Policy: {selectedExtension.PolicyIdNo}</p>
                <h3>Extension Details</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Policy Number:</span>
                    <strong>{selectedExtension.PolicyNumber}</strong>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Registration:</span>
                    <strong>{selectedExtension.Registration}</strong>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Gross Premium:</span>
                    <strong className="amount">{formatCurrency(selectedExtension.GrossPremium)}</strong>
                  </div>
                </div>
              </div>

              {approvalResult ? (
                <div className={`certificate-ready ${approvalResult.warning ? 'has-warning' : ''}`}>
                  <h3>{approvalResult.certUrl ? '🎉 Certificate Ready' : '✅ Extension Approved'}</h3>
                  <p>{approvalResult.message}</p>
                  {approvalResult.warning && (
                    <p className="result-warning">⚠️ {approvalResult.warning}</p>
                  )}
                  {approvalResult.certUrl && (
                    <a
                      href={approvalResult.certUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="primary-btn"
                    >
                      ⬇️ Download Certificate
                    </a>
                  )}
                </div>
              ) : (
                <div className="payment-method-selection">
                  <h3>Select Payment Method</h3>

                  {submissionError && (
                    <div className="error-banner">{submissionError}</div>
                  )}

                  <div className="payment-options">
                    {PAYMENT_METHODS
                      .filter((method) => !method.definiteOnly || isDefiniteUnderwriter)
                      .map((method) => (
                        <label
                          key={method.value}
                          className={`payment-option ${paymentMethod === method.value ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.value}
                            checked={paymentMethod === method.value}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                          />
                          <div className="payment-option-content">
                            <span className="payment-icon">{method.icon}</span>
                            <div>
                              <strong>{method.label}</strong>
                              <p>{method.description}</p>
                              {method.balanceField && (
                                <p className="payment-balance">
                                  {loadingBalances
                                    ? 'Checking balance...'
                                    : `Available: ${new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(definiteBalances?.[method.balanceField] ?? 0)}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>

                  {paymentMethod === 'transaction' && (
                    <div className="payment-input-section">
                      <label>Transaction Code *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter M-Pesa transaction code (e.g., SJ4K5L6M7N)"
                        value={transactionCode}
                        onChange={(e) => setTransactionCode(e.target.value.toUpperCase())}
                      />
                      <p className="input-hint">Enter the M-Pesa confirmation code provided by the customer</p>
                    </div>
                  )}

                  {paymentMethod === 'mpesa' && (
                    <div className="payment-input-section">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="Enter phone number (e.g., 254712345678)"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                      <p className="input-hint">Customer will receive STK push to complete payment</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              {approvalResult ? (
                <button className="primary-btn" onClick={handleClosePaymentModal}>
                  Done
                </button>
              ) : (
                <>
                  <button className="secondary-btn" onClick={handleClosePaymentModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    className="primary-btn"
                    onClick={handleSubmitPayment}
                    disabled={submitting || !paymentMethod}
                  >
                    {submitting ? '⏳ Processing...' : '✓ Confirm & Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingExtensions;
